import { TASK_PRIORITIES, type Task } from '@allemnii-pulse/shared';

export interface ScheduledTask {
  taskId: string;
  /** Working days from the project start, fractional: 0.5 is midday of the first day. */
  startDayOffset: number;
  endDayOffset: number;
}

const HOURS_PER_DAY = 8;
const DEFAULT_ESTIMATE_HOURS = 4;

/**
 * A simple forward-scheduling pass in working hours: each task starts the moment every task it
 * depends on finishes (or at 0 if it has no dependencies), and runs for exactly estimateHours, at
 * 8 working hours a day. Offsets count working days only; weekends are added when converting to
 * dates (see toCalendarSpan). Offsets are NOT rounded to whole days — coding tasks are estimated for a
 * developer with a coding agent and are often an hour or two, so a chain of them fits in one day.
 * There is no per-task start/due date in the schema (see docs/PLAN.md §13 item 6 — "Timeline is
 * read-only in v1"), so this derives a schedule from the real dependency graph and estimates
 * instead of inventing dates. A cyclic graph (shouldn't happen — see taskGraph validation on the
 * server) breaks out defensively rather than looping forever.
 */
export function scheduleTasks(tasks: Task[]): Map<string, ScheduledTask> {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const result = new Map<string, ScheduledTask>();
  const visiting = new Set<string>();

  function resolve(taskId: string): ScheduledTask {
    const cached = result.get(taskId);
    if (cached) return cached;
    const task = byId.get(taskId);
    const fallback = { taskId, startDayOffset: 0, endDayOffset: DEFAULT_ESTIMATE_HOURS / HOURS_PER_DAY };
    if (!task) return fallback;
    if (visiting.has(taskId)) return fallback; // cycle guard

    visiting.add(taskId);
    let start = 0;
    for (const depId of task.dependsOn) {
      if (!byId.has(depId)) continue;
      const dep = resolve(depId);
      start = Math.max(start, dep.endDayOffset);
    }
    visiting.delete(taskId);

    const durationDays = (task.estimateHours ?? DEFAULT_ESTIMATE_HOURS) / HOURS_PER_DAY;
    const scheduled: ScheduledTask = { taskId, startDayOffset: start, endDayOffset: start + durationDays };
    result.set(taskId, scheduled);
    return scheduled;
  }

  for (const t of tasks) resolve(t.id);
  return result;
}

const PRIORITY_RANK = new Map(TASK_PRIORITIES.map((p, i) => [p, i]));

/**
 * Orders tasks by priority (critical → low) while respecting the dependency graph: a task never
 * appears before a task it depends on. This is a Kahn's-algorithm topological sort where, instead
 * of a FIFO queue, the next task taken from the "ready" set (all its dependencies already placed)
 * is always the highest-priority one. A cyclic or dangling dependency falls back to placing
 * whatever remains in priority order rather than dropping tasks.
 */
export function sortTasksByPriority(tasks: Task[]): Task[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const remaining = new Set(tasks.map((t) => t.id));
  const result: Task[] = [];

  function isReady(task: Task): boolean {
    return task.dependsOn.every((depId) => !byId.has(depId) || !remaining.has(depId));
  }

  function compare(a: Task, b: Task): number {
    const rankDiff = (PRIORITY_RANK.get(a.priority) ?? 0) - (PRIORITY_RANK.get(b.priority) ?? 0);
    if (rankDiff !== 0) return rankDiff;
    return a.order - b.order;
  }

  while (remaining.size > 0) {
    // Take ONE task per pass: placing it may unblock a higher-priority dependent, which must then
    // beat the lower-priority tasks that were already ready.
    const ready = tasks.filter((t) => remaining.has(t.id) && isReady(t));
    const pool = ready.length > 0 ? ready : tasks.filter((t) => remaining.has(t.id));
    const next = pool.reduce((best, t) => (compare(t, best) < 0 ? t : best));
    result.push(next);
    remaining.delete(next.id);
  }

  return result;
}

export function isWeekend(d: Date): boolean {
  return d.getDay() === 0 || d.getDay() === 6;
}

/** Calendar days from the project start to its n-th working day (0-based), skipping Saturdays
 * and Sundays — a project starting on a weekend begins on the following Monday. */
function workdayToCalendarDay(projectStart: string, n: number): number {
  let workdays = -1;
  for (let day = 0; ; day++) {
    if (!isWeekend(addDays(projectStart, day)) && ++workdays === n) return day;
  }
}

/** A scheduled task's span in fractional CALENDAR days from the project start, for drawing it on
 * a calendar axis. An end on a day boundary stays at the end of that working day, so a task
 * finishing on Friday never stretches over the weekend. */
export function toCalendarSpan(projectStart: string, s: ScheduledTask): { start: number; end: number } {
  const startDay = Math.floor(s.startDayOffset);
  const endDay = Math.max(startDay, Math.ceil(s.endDayOffset) - 1);
  return {
    start: workdayToCalendarDay(projectStart, startDay) + (s.startDayOffset - startDay),
    end: workdayToCalendarDay(projectStart, endDay) + (s.endDayOffset - endDay),
  };
}

/** The calendar days a scheduled task occupies, both inclusive: a task from hour 1 to hour 3 of
 * the first working day starts and ends on that day. */
export function scheduledDates(projectStart: string, s: ScheduledTask): { start: Date; end: Date } {
  const span = toCalendarSpan(projectStart, s);
  const startDay = Math.floor(span.start);
  const endDay = Math.max(startDay, Math.ceil(span.end) - 1);
  return { start: addDays(projectStart, startDay), end: addDays(projectStart, endDay) };
}

export function addDays(iso: string, days: number): Date {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d;
}
